/**
 * The one date-order setting for the whole screen.
 *
 * A typed `11/05/2026` is 5 November in the US and 11 May in the UK, so the
 * date box (`design-system/cos-date-picker.ts` and `date-parse.ts`) must know
 * which the person means. The order is a property of the chosen LIBRARY, not
 * of each box: the app root resolves it once from the library's country when
 * the library changes and calls `setDateOrder`; the date control reads
 * `getDateOrder()`, so none of its call sites pass anything.
 *
 * Month first (`mdy`) is the default, and is what a Communico staff member
 * with no library chosen, an unknown country and every unlisted country get.
 */

export type DateOrder = 'mdy' | 'dmy';

/** Fired on `window` when the active order changes, so open boxes redraw their hint. */
export const DATE_ORDER_CHANGED_EVENT = 'cos:date-order-changed';

// Day-first countries, as two-letter codes. UK is not an ISO code but is
// what people type into a country field, so it is accepted beside GB.
const DAY_FIRST_CODES = new Set(['GB', 'UK', 'AU', 'NZ', 'IE']);

const DAY_FIRST_NAMES = new Set([
	'united kingdom',
	'great britain',
	'england',
	'scotland',
	'wales',
	'northern ireland',
	'australia',
	'new zealand',
	'ireland',
	'republic of ireland',
]);

/** Maps a library's country (a code or a name, any case) to its date order. */
export function dateOrderFromCountry(country: string | null | undefined): DateOrder {
	const c = (country ?? '').trim();
	if (!c) return 'mdy';
	if (DAY_FIRST_CODES.has(c.toUpperCase()) || DAY_FIRST_NAMES.has(c.toLowerCase())) return 'dmy';
	return 'mdy';
}

let active: DateOrder = 'mdy';

export function getDateOrder(): DateOrder {
	return active;
}

export function setDateOrder(order: DateOrder): void {
	if (order === active) return;
	active = order;
	if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(DATE_ORDER_CHANGED_EVENT));
}

/** What the shell calls with the chosen library's country. */
export function setDateOrderFromCountry(country: string | null | undefined): void {
	setDateOrder(dateOrderFromCountry(country));
}

/** Test-only: back to the month-first default without firing an event. */
export function _resetDateOrderForTests(): void {
	active = 'mdy';
}

/** A full example date in the order, for hints: 10/31/2026 or 31/10/2026. */
export function dateOrderExample(order: DateOrder = active, year = 2026): string {
	return order === 'mdy' ? `10/31/${year}` : `31/10/${year}`;
}

/** A date example with no year, for the combined date-and-time hint. */
export function dateOrderExampleShort(order: DateOrder = active): string {
	return order === 'mdy' ? '10/31' : '31/10';
}

/** A short range example in typed numbers: 10/05 to 10/09 or 05/10 to 09/10. */
export function dateOrderRangeExample(order: DateOrder = active): string {
	return order === 'mdy' ? '10/05 to 10/09' : '05/10 to 09/10';
}

/** The sentence under a box when typed numbers cannot be a date in this order. */
export function dateOrderMessage(order: DateOrder = active): string {
	return order === 'mdy'
		? 'Enter the date as month/day/year, like 10/31/2026.'
		: 'Enter the date as day/month/year, like 31/10/2026.';
}
